import { useState, useCallback } from 'react';
import { TETROMINOS, randomTetromino } from '../tetrominos';
import { STAGE_WIDTH, checkCollision } from '../gameHelpers';
import { StageType } from '../types';
import { logMessage } from '../utils/logger';

export type PlayerType = {
  pos: {
    x: number;
    y: number;
  };
  tetromino: (string | number)[][];
  collided: boolean;
};

export const usePlayer = (nextTetrominoShape?: (string | number)[][], setNextTetromino?: (shape: (string | number)[][]) => void) => {
  const [player, setPlayer] = useState({
    pos: { x: 0, y: 0 },
    tetromino: TETROMINOS[0].shape,
    collided: false,
  } as PlayerType);

  const rotate = (matrix: (string | number)[][], dir: number) => {
    // Make the rows become cols (transpose)
    const rotatedTetro = matrix.map((_, index) =>
      matrix.map(col => col[index])
    );
    // Reverse each row to get a rotated matrix
    if (dir > 0) return rotatedTetro.map(row => row.reverse());
    return rotatedTetro.reverse();
  };

  const playerRotate = (stage: StageType, dir: number) => {
    const clonedPlayer = JSON.parse(JSON.stringify(player));
    clonedPlayer.tetromino = rotate(clonedPlayer.tetromino, dir);

    const pos = clonedPlayer.pos.x;
    let offset = 1;
    while (checkCollision(clonedPlayer, stage, { x: 0, y: 0 })) {
      clonedPlayer.pos.x += offset;
      offset = -(offset + (offset > 0 ? 1 : -1));
      if (offset > clonedPlayer.tetromino[0].length) {
        rotate(clonedPlayer.tetromino, -dir);
        clonedPlayer.pos.x = pos;
        return;
      }
    }

    setPlayer(clonedPlayer);
    logMessage(`Player rotated. New position: x=${clonedPlayer.pos.x}, y=${clonedPlayer.pos.y}`);
  };

 const updatePlayerPos = ({ x, y, collided }: { x: number; y: number; collided: boolean }) => {
    setPlayer(prev => {
      const newX = prev.pos.x + x;
      const newY = prev.pos.y + y;
      const newPlayer = {
        ...prev,
        pos: { x: newX, y: newY },
        collided,
      };
      logMessage(`New player position: x=${newPlayer.pos.x}, y=${newPlayer.pos.y}, collided=${newPlayer.collided}`);
      return newPlayer;
    });
  };

  const resetPlayer = useCallback(() => {
    // Use the next tetromino if provided, otherwise generate a random one
    const currentTetromino = nextTetrominoShape || randomTetromino().shape;
    
    // Generate the next tetromino for preview
    const newNextTetromino = randomTetromino().shape;
    if (setNextTetromino) {
      setNextTetromino(newNextTetromino);
    }
    
    // Calculate the correct starting position based on tetromino width
    const tetrominoWidth = currentTetromino[0].length;
    const startX = Math.floor(STAGE_WIDTH / 2 - tetrominoWidth / 2);
    
    setPlayer({
      pos: { x: startX, y: 0 },
      tetromino: currentTetromino,
      collided: false,
    });
  }, [nextTetrominoShape, setNextTetromino]);

  return [player, updatePlayerPos, resetPlayer, playerRotate] as const;
};
